import { skipToken, useMutation } from '@apollo/client'
import { Loader } from 'lucide-react'
import { Suspense, useTransition } from 'react'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import { ToasterContent } from '@/components/sv/toaster-content'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { gql } from '@/lib/graphql/gql'
import { useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import { RemoveExceptionDialogFallback } from '@/routes/single-sign-on/exceptions/remove-exception-dialog/fallback'
import type {
  RemoveSsoExceptionQuery,
  RemoveSsoExceptionQueryVariables,
  RemoveUsersFromSamlExceptionsMutation,
  RemoveUsersFromSamlExceptionsMutationVariables,
} from '@/routes/single-sign-on/exceptions/remove-exception-dialog/index.types.gen'

const REMOVE_EXCEPTION_QUERY = gql`
  query RemoveSsoException($idCompany: String!) {
    company(id: $idCompany) {
      id
      users {
        id
        capabilities @live {
          id
          isSamlExempt
        }
        user {
          id
          email
          firstName
          lastName
        }
      }
    }
  }
`

const REMOVE_USERS_FROM_SAML_EXCEPTIONS_MUTATION = gql`
  mutation RemoveUsersFromSamlExceptions(
    $idUsers: [String!]!
    $idCompany: String!
  ) {
    removeUsersFromSamlExceptions(idUsers: $idUsers, idCompany: $idCompany) {
      id
      isSamlExempt
    }
  }
`

type Props = {
  isOpen: boolean
  onClose: () => void
}

const DialogContent = ({ isOpen, onClose }: Props) => {
  const { idCompany } = useIdCompany()
  const { idUser: removeIdUser } = useParams()
  const [isPending, startTransition] = useTransition()

  const { data } = useSuspenseQuery<
    RemoveSsoExceptionQuery,
    RemoveSsoExceptionQueryVariables
  >(REMOVE_EXCEPTION_QUERY, isOpen ? { variables: { idCompany } } : skipToken)

  const [removeUsersFromExceptions] = useMutation<
    RemoveUsersFromSamlExceptionsMutation,
    RemoveUsersFromSamlExceptionsMutationVariables
  >(REMOVE_USERS_FROM_SAML_EXCEPTIONS_MUTATION)

  const users = data?.company?.users ?? []

  const exceptionUsers = users
    .filter((cu) => cu.capabilities?.some((c) => c.isSamlExempt))
    .map((cu) => ({
      id: cu.id,
      idUser: cu.user?.id ?? '',
      email: cu.user?.email ?? '',
      firstName: cu.user?.firstName ?? '',
      lastName: cu.user?.lastName ?? '',
    }))

  const user = exceptionUsers.find((u) => u.idUser === removeIdUser)

  return (
    <>
      <AlertDialogHeader>
        <AlertDialogTitle>Remove exception</AlertDialogTitle>
        <AlertDialogDescription>
          Are you sure you want to remove{' '}
          <strong>
            {user?.firstName} {user?.lastName}
          </strong>{' '}
          from the single sign-on exceptions list?
        </AlertDialogDescription>
      </AlertDialogHeader>
      <p className="text-sm text-muted-foreground">
        When removed, the user will only be able to access this company by
        authenticating with the configured identity provider.
      </p>
      {exceptionUsers.length === 1 && (
        <Alert>
          <AlertDescription>
            This will remove the last user from the exceptions list. We
            recommend that you have at least one user in your organization who
            can access the account with a password, in case there is an issue
            with your identity provider and you need to regain access to your
            account.
          </AlertDescription>
        </Alert>
      )}
      <AlertDialogFooter>
        <AlertDialogCancel disabled={isPending} onClick={() => onClose()}>
          Cancel
        </AlertDialogCancel>
        <AlertDialogAction
          disabled={isPending}
          onClick={() => {
            startTransition(async () => {
              try {
                if (!user?.idUser) {
                  return
                }
                await removeUsersFromExceptions({
                  variables: { idUsers: [user.idUser], idCompany },
                })
                toast(
                  <ToasterContent variant="success">
                    Exception removed successfully
                  </ToasterContent>,
                  { position: 'top-right' }
                )
                onClose()
              } catch {
                toast(
                  <ToasterContent variant="error">
                    Failed to remove exception
                  </ToasterContent>,
                  { position: 'top-right' }
                )
              }
            })
          }}
        >
          {isPending ? <Loader className="animate-spin" /> : null}
          Continue
        </AlertDialogAction>
      </AlertDialogFooter>
    </>
  )
}

export const RemoveExceptionDialog = ({ isOpen, onClose }: Props) => {
  return (
    <AlertDialog open={isOpen}>
      <AlertDialogContent onEscapeKeyDown={onClose}>
        <Suspense fallback={<RemoveExceptionDialogFallback />}>
          <DialogContent isOpen={isOpen} onClose={onClose} />
        </Suspense>
      </AlertDialogContent>
    </AlertDialog>
  )
}
